import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import { Router } from '@angular/router';

import { User, LoginRequest, LoginResponse, RegisterRequest, UserDTO } from '../models/user.model';
import { StorageService } from './storage.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly TOKEN_KEY = 'hms_token';
  private readonly USER_KEY = 'hms_user';

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private router: Router
  ) {
    this.loadStoredUser();
  }

  private loadStoredUser(): void {
    const user = this.storageService.get(this.USER_KEY);
    if (user) {
      try {
        this.currentUserSubject.next(JSON.parse(user));
      } catch {
        this.storageService.remove(this.USER_KEY);
      }
    }
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/login`, credentials)
      .pipe(
        tap(response => this.handleAuthResponse(response))
      );
  }

  register(data: RegisterRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/register`, data)
      .pipe(
        tap(response => this.handleAuthResponse(response))
      );
  }

  private handleAuthResponse(response: LoginResponse): void {
  
    this.storageService.set(this.TOKEN_KEY, response.accessToken);
    

    const user: User = {
      id: response.userDTO.id,
      email: response.userDTO.email,
      username: response.userDTO.username,
      firstName: response.userDTO.firstName,
      lastName: response.userDTO.lastName,
      enabled: response.userDTO.enabled,
      //roles: Array.isArray(response.userDTO.roles) ? response.userDTO.roles : Array.from(response.userDTO.roles)
    };
    
    this.storageService.set(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  logout(): void {
    this.storageService.remove(this.TOKEN_KEY);
    this.storageService.remove(this.USER_KEY);
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  getToken(): string | null {
    return this.storageService.get(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    if (!user?.roles) return false;
    const rolesArray = Array.isArray(user.roles) ? user.roles : Array.from(user.roles);
    return rolesArray.includes(role);
  }

  isGuest(): boolean {
    return this.hasRole('ROLE_USER') || this.hasRole('ROLE_CLIENT');
  }

  refreshToken(): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/refresh`, {})
      .pipe(
        tap(response => this.handleAuthResponse(response))
      );
  }

  updateProfile(data: Partial<User>): Observable<User> {
    return this.http.patch<User>(`${this.API_URL}/auth/profile`, data)
      .pipe(
        tap(user => {
          this.storageService.set(this.USER_KEY, JSON.stringify(user));
          this.currentUserSubject.next(user);
        })
      );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.API_URL}/auth/change-password`, {
      currentPassword,
      newPassword
    });
  }
}
